import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    StorePageDomainRequestSchema,
    UpdatePageDomainRequestSchema,
    type StorePageDomainRequest,
    type UpdatePageDomainRequest,
} from "@/types/PageDomains";
import type { z } from "zod";

type ValidationResult<T> =
    | { success: true; data: T }
    | { success: false; errors: z.ZodError };

type RequestOptions = {
    onSuccess?: () => void;
    onError?: (errors: Record<string, string>) => void;
    onFinish?: () => void;
};

export function usePageDomains() {
    const createPageDomain = (
        payload: StorePageDomainRequest,
        options?: RequestOptions,
    ): ValidationResult<StorePageDomainRequest> => {
        const result = StorePageDomainRequestSchema.safeParse(payload);

        if (!result.success) {
            return { success: false, errors: result.error };
        }

        router.post(route("app.page_domains.store"), result.data, {
            onSuccess: () => options?.onSuccess?.(),
            onError: (errors) =>
                options?.onError?.(errors as Record<string, string>),
            onFinish: () => options?.onFinish?.(),
        });

        return { success: true, data: result.data };
    };

    const updatePageDomain = (
        id: number,
        payload: UpdatePageDomainRequest,
        options?: RequestOptions,
    ): ValidationResult<UpdatePageDomainRequest> => {
        const result = UpdatePageDomainRequestSchema.safeParse(payload);

        if (!result.success) {
            return { success: false, errors: result.error };
        }

        router.put(
            route("app.page_domains.update", { pageDomain: id }),
            result.data,
            {
                onSuccess: () => options?.onSuccess?.(),
                onError: (errors) =>
                    options?.onError?.(errors as Record<string, string>),
                onFinish: () => options?.onFinish?.(),
            },
        );

        return { success: true, data: result.data };
    };

    const deletePageDomain = (id: number, options?: RequestOptions): void => {
        router.delete(route("app.page_domains.delete", { pageDomain: id }), {
            onSuccess: () => options?.onSuccess?.(),
            onError: (errors) =>
                options?.onError?.(errors as Record<string, string>),
            onFinish: () => options?.onFinish?.(),
        });
    };

    const setDefaultPageDomain = (
        id: number,
        options?: RequestOptions,
    ): void => {
        router.post(
            route("app.page_domains.set_default", { pageDomain: id }),
            {},
            {
                onSuccess: () => options?.onSuccess?.(),
                onError: (errors) =>
                    options?.onError?.(errors as Record<string, string>),
                onFinish: () => options?.onFinish?.(),
            },
        );
    };

    const verifyPageDomain = (id: number, options?: RequestOptions): void => {
        router.post(
            route("app.page_domains.verify", { pageDomain: id }),
            {},
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => options?.onSuccess?.(),
                onError: (errors) =>
                    options?.onError?.(errors as Record<string, string>),
                onFinish: () => options?.onFinish?.(),
            },
        );
    };

    return {
        createPageDomain,
        updatePageDomain,
        deletePageDomain,
        setDefaultPageDomain,
        verifyPageDomain,
    };
}
